/**
 * Entry representing a single bill in the build-time index.
 *
 * Generated automatically at build via the `build-bills-index` step.
 */
export interface BillIndexEntry {
  // Bill filename, e.g. "hres741"
  id: string;
  // Relative path to the JSON file under `/data/congress###/bills/`
  file: string;
  // Bill title
  title: string;
  // Congress session for this bill
  congress: number;
  // Bill description
  description: string;
  // Bill keywords, currently just a length=1 list of a unique String ("test" + id) for testing
  keywords: string[];
  //recommended metrics to demo with selected bill
  recommended: string[];
}

/**
 * Full list of available bills generated during build.
 * Stored as `/data/congress###/bills/index.json`.
 */
export type BillIndex = BillIndexEntry[];

/**
 * Entry representing a single metric in the build-time index.
 *
 * Generated automatically at build via the `build-metric-index` step.
 */
export interface MetricIndexEntry {
  // Metric key, e.g. "veteran_count"
  id: string;
  // Relative path to the JSON file under `/data/congress###/metrics/`
  file: string;
  // Metric title
  title: string;
  // Congress session for this metric
  congress: number;
}

/**
 * Full list of available metrics generated during build.
 * Stored as `/data/congress###/metrics/index.json`.
 */
export type MetricIndex = MetricIndexEntry[];

/**
 * Enumerated set of normalized vote outcomes.
 *
 * These are standardized during parsing so that map and chart components
 * can reliably color or count votes independent of raw string values.
 */
export const VoteType = {
  YEA: 'Yea',
  NAY: 'Nay',
  NO_VOTE: 'No Vote',
  NO_DATA: 'No Data',
} as const;

export type VoteType = (typeof VoteType)[keyof typeof VoteType];

/**
 * Normalized view of vote and metric details for a single district.
 */
export type DistrictData = Partial<{
  // District identifier (`<congressId>-<state>-<district>`)
  districtId: DistrictId;
  // Raw vote outcome for the selected bill
  voteRaw: string;
  // Normalized vote outcome
  voteType: VoteType;
  // Full state name, e.g. "Georgia"
  stateName: string;
  // Numeric state district
  districtNum: number;
  // Congress session number
  congressId: number;
  // Bill identifier, e.g. "hres741"
  billId: string;
  // Legislator display name, e.g. "McCormick, Richard"
  legislatorName: string;
  // Legislator BioGuideID, e.g. "M001218"
  legislatorId: string;
  // Legislator party, e.g. "Republican"
  legislatorParty: string;
  // URL for additional legislator info
  legislatorLink: string;
  // Legislator image URL, e.g., "https://www.congress.gov/img/member/m001218_200.jpg"
  legislatorImageLink: string;
  // Legislator image attribution, e.g. "Image courtesy of the Member"
  legislatorImageSource: string;
  // Metric label, e.g. "Population Veteran Percentage"
  metricLabel: string;
  // Metric value, e.g. 11
  metricValue: number;
}>;

/**
 * Aggregate statistics calculated across all districts for a selected metric.
 */
export interface MetricAggregates {
  // Maximum metric value across all districts
  metricMax: number;
  // Minimum metric value across all districts
  metricMin: number;
  // Average metric value across all districts
  metricAvg: number;
  // Variance of metric values across all districts
  metricVar: number;
  // count, just for calculation references
  count: number;
  // alpha value for t-tests
  alpha: number;
  // Average metric value for districts with supportive vote
  voteSupportMetricAvg: number;
  // Average metric value for districts with opposing vote
  voteAgainstMetricAvg: number;
  // Average metric value for districts with no vote
  voteAbsenceMetricAvg: number;
  // Variance of metric values for districts with supportive vote
  voteSupportMetricVar: number;
  // Variance of metric values for districts with opposing vote
  voteAgainstMetricVar: number;
  // Variance of metric values for districts with no vote
  voteAbsenceMetricVar: number;
  // Relevant values to t-test for districts with supportive vote
  voteSupportMetricTtest: number[];
  // Relevant values to t-test for districts with opposing vote
  voteAgainstMetricTtest: number[];
  // Relevant values to t-test for districts with no vote
  voteAbsenceMetricTtest: number[];
  // Count of districts with supportive vote
  yeaCount: number;
  // Count of districts with no vote
  noVoteCount: number;
  // Count of districts with opposing vote
  nayCount: number;
}

export interface MetricStatistics {}

export interface StatsBundle {
  tstat: number[];
  p: number[];
  df: number[];
  significant: string[];
}

/**
 * Unique identifier for a congressional district within a specific congress.
 *
 * Convention: `<congressId>-<stateAbbrev>-<districtNum>`
 * Example: `"118-ga-06"`
 */
export type DistrictId = `${number}-${Lowercase<string>}-${string}`;

/**
 * Dictionary of district data keyed by `DistrictId`.
 */
export type DistrictDict = Record<DistrictId, DistrictData>;

/**
 * Visualization options determining what/how data is displayed
 */
export type MetricColorScheme = 'Blues' | 'Greens' | 'Oranges' | 'Purples' | 'Reds' | 'Greys';

export type VizOptions = Partial<{
  showPoints: boolean;
  // Source used to color district polygons
  polyColor: 'voteType' | 'metric';
  // Source used to color district point overlays
  pointsColor: 'voteType' | 'metric';
  // Color for votes supporting bill (e.g. "#80CFA9", green)
  voteSupportColor: string;
  // Color for votes opposing bill (e.g. "#cccc1b", yellow)
  voteAgainstColor: string;
  // Color for no vote on bill (e.g. "#EEEEEE", gray)
  voteAbsenceColor: string;
  // Color for no data for district vote
  voteNoDataColor: string;
  // Number of bins for polygon gradient, D3 library mostly supports 3-8
  binNum: number;
  // Whether to use discrete bins (true) or continuous gradient (false) for metric colors
  useBins: boolean;
  // Name of the D3 sequential color scheme applied to metrics
  metricColorScheme: MetricColorScheme;
  dataDrawerOpen: boolean;
  drawerOpen: boolean;
  useFeatureLabel: boolean;
}>;
